import { Timestamp, updateDoc, doc, writeBatch, deleteDoc, collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from 'lib/firebase';

/**
 * Custom hook for queue management operations
 * @param {Function} setSnackbar - Function to show snackbar messages
 * @returns {Object} Queue management functions
 */
export const useQueueManagement = (setSnackbar) => {
  
  /**
   * Get next Sunday at 9 AM CST
   */
  const getNextSunday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 7 : 7 - dayOfWeek;
    const nextSunday = new Date(today);
    nextSunday.setDate(today.getDate() + daysUntilSunday);
    nextSunday.setHours(9, 0, 0, 0); // 9 AM
    return nextSunday;
  };

  /**
   * Reorder queue and recalculate publish dates
   */
  const handleQueueReorder = async (reorderedQuestions) => {
    const batch = writeBatch(db);
    const nextSunday = getNextSunday();

    reorderedQuestions.forEach((question, index) => {
      const queueOrder = index + 1;
      const weeksToAdd = index * 7 * 24 * 60 * 60 * 1000;
      const publishDate = new Date(nextSunday.getTime() + weeksToAdd);

      batch.update(doc(db, 'weekly_questions', question.id), {
        queueOrder,
        publishDate: Timestamp.fromDate(publishDate),
        status: 'queued'
      });
    });

    try {
      await batch.commit();
      setSnackbar({ open: true, message: 'Queue updated successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update queue', severity: 'error' });
    }
  };

  /**
   * Promote suggestion to end of queue
   */
  const handlePromote = async (suggestion, questions) => {
    try {
      // Find max queueOrder
      const queuedQuestions = questions.filter(q => q.queueOrder);
      const maxOrder = queuedQuestions.length > 0 
        ? Math.max(...queuedQuestions.map(q => q.queueOrder))
        : 0;
      
      const newQueueOrder = maxOrder + 1;
      const nextSunday = getNextSunday();
      const weeksToAdd = maxOrder * 7 * 24 * 60 * 60 * 1000;
      const publishDate = new Date(nextSunday.getTime() + weeksToAdd);

      await updateDoc(doc(db, 'weekly_questions', suggestion.id), {
        status: 'queued',
        queueOrder: newQueueOrder,
        publishDate: Timestamp.fromDate(publishDate),
        promotedAt: Timestamp.now(),
      });

      setSnackbar({ open: true, message: 'Question added to queue', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to promote question', severity: 'error' });
    }
  };

  /**
   * Remove question from queue (depromote)
   */
  const handleDepromote = async (questionId) => {
    try {
      await updateDoc(doc(db, 'weekly_questions', questionId), {
        queueOrder: null,
        publishDate: null,
        status: 'draft'
      });

      setSnackbar({ open: true, message: 'Question removed from queue', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to remove from queue', severity: 'error' });
    }
  };

  /**
   * Send question immediately
   */
  const handleSendNow = async (questionId) => {
    try {
      const triggerEmail = httpsCallable(functions, 'trigger_email_manually');
      await triggerEmail({ questionId });

      // Remove from queue
      await updateDoc(doc(db, 'weekly_questions', questionId), {
        queueOrder: null,
        status: 'sent'
      });

      setSnackbar({ open: true, message: 'Email sent successfully!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Failed to send email', severity: 'error' });
    }
  };

  /**
   * Delete suggestion
   */
  const handleReject = async (suggestionId) => {
    if (!window.confirm('Are you sure you want to delete this suggestion?')) return;
    
    try {
      await deleteDoc(doc(db, 'weekly_questions', suggestionId));
      setSnackbar({ open: true, message: 'Suggestion deleted', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete', severity: 'error' });
    }
  };

  /**
   * Undo answer - reset answered question back to 'sent' status
   */
  const handleUndoAnswer = async (questionId) => {
    try {
      // First, delete all comments in the subcollection
      const commentsRef = collection(db, 'weekly_questions', questionId, 'comments');
      const commentsSnapshot = await getDocs(commentsRef);
      
      // Use batch to delete all comments
      const batch = writeBatch(db);
      commentsSnapshot.docs.forEach((commentDoc) => {
        batch.delete(commentDoc.ref);
      });
      await batch.commit();

      // Then update the question document
      await updateDoc(doc(db, 'weekly_questions', questionId), {
        status: 'sent',
        momAnswer: null,
        answerDate: null,
        answeredBy: null,
        photoUrls: null,
        comments: null,
        isVisible: null
      });

      setSnackbar({ open: true, message: 'Answer undone - question reset to sent', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to undo answer', severity: 'error' });
    }
  };

  return {
    handleQueueReorder,
    handlePromote,
    handleDepromote,
    handleSendNow,
    handleReject,
    handleUndoAnswer,
    getNextSunday,
  };
};
